import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from './config';
import { supabase } from './supabase';
import {
  s, calculateOxidationRisk, getRiskLevel, getPredictedFinishDate,
  getPourCount, getPreservationLabel, getProofAdjustmentLabel,
  formatDate, getScoreColor, getFillColor, getAvgScore,
  FlavorSection, TastingSlider, StepIndicator,
  BottleCard, DeleteBottleModal, DeleteNoteModal,
  TwoTierChips, ExpansionCard, ColorSlider, MultiChips,
} from './components';
import {
  WHISKEY_TYPES, FINISH_CASKS, PRESERVATION_METHODS,
  NOSE_FLAVORS, PALATE_FLAVORS, FINISH_FLAVORS,
  REASON_TAGS, SERVING_STYLES,
  MOUTHFEEL_OPTIONS, NOSE_INTENSITY_OPTIONS, HEAT_OPTIONS,
  COLOR_STOPS, FINISH_LENGTH_OPTIONS, FINISH_FADE_OPTIONS,
  CLARITY_OPTIONS, VISCOSITY_OPTIONS,
} from './constants';

const TASTING_DRAFT_KEY = 'barrelnotes_tasting_draft';
const editDraftKey = (noteId) => `barrelnotes_edit_draft_${noteId}`;

// ── Phase accent colors — all amber family ──
const PHASE_COLORS = {
  color:  '#d4a96a',
  nose:   '#d4a96a',
  palate: '#d4a96a',
  finish: '#d4a96a',
};

const db = {
  fetch: async (table, columns = '*', filters = {}) => {
    let query = supabase.from(table).select(columns);
    Object.entries(filters).forEach(([key, value]) => { query = query.eq(key, value); });
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },
  insert: async (table, record) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from(table).insert({ ...record, user_id: user?.id }).select();
    if (error) throw error;
    return data;
  },
  update: async (table, id, record) => {
    const { data, error } = await supabase.from(table).update(record).eq('id', id).select();
    if (error) throw error;
    return data;
  },
  delete: async (table, column, value) => {
    const { error } = await supabase.from(table).delete().eq(column, value);
    if (error) throw error;
  },
};

const lookupBottle = async (bottleName) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const response = await fetch('/api/lookup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ bottleName }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data;
};

const FINISHED_COLOR = '#7B52A0';

const OptionChips = ({ options, selected, onSelect }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
    {options.map(opt => {
      const active = selected === opt;
      return (
        <button key={opt} onClick={() => onSelect(active ? null : opt)} style={{
          padding: '8px 16px', borderRadius: 20,
          border: `1px solid ${active ? COLORS.accent : 'rgba(144,115,85,0.35)'}`,
          background: active ? `linear-gradient(135deg, ${COLORS.accentLight}22, ${COLORS.accent}33)` : 'transparent',
          color: active ? COLORS.accentLight : COLORS.textMuted,
          fontSize: 13, fontFamily: 'Georgia, serif', cursor: 'pointer',
          fontWeight: active ? 'bold' : 'normal', transition: 'all 0.15s',
        }}>{opt}</button>
      );
    })}
  </div>
);

// ── PhaseCard — collapsible card with top border accent and mini-summary ──
const PhaseCard = ({ phaseKey, label, isOpen, onToggle, summary, children }) => {
  const accentColor = PHASE_COLORS[phaseKey] || COLORS.accent;
  return (
    <div style={{
      ...s.card,
      padding: 0,
      borderTop: `3px solid ${accentColor}`,
      marginBottom: 8,
      overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '14px 16px',
          cursor: 'pointer',
          minHeight: 52,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, color: accentColor, letterSpacing: 3,
            textTransform: 'uppercase', fontFamily: 'Georgia, serif',
            fontWeight: 'bold', marginBottom: 5,
          }}>{label}</div>
          <MiniSummary summary={summary} />
        </div>
        <span style={{
          color: COLORS.textDim, fontSize: 11,
          marginLeft: 10, marginTop: 2, flexShrink: 0,
          transition: 'transform 0.2s',
          display: 'inline-block',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>▼</span>
      </div>
      {isOpen && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '14px 16px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── MiniSummary — renders collapsed state content ──
const MiniSummary = ({ summary }) => {
  if (!summary || summary.length === 0) {
    return <span style={{ fontSize: 12, color: COLORS.textDim, fontStyle: 'italic' }}>tap to fill</span>;
  }
  const Dot = () => <span style={{ color: COLORS.textDim, fontSize: 11, margin: '0 3px' }}>·</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      {summary.map((item, i) => {
        const showDot = i < summary.length - 1;
        if (item.type === 'broad-only') {
          return (
            <React.Fragment key={i}>
              <span style={{
                fontSize: 12, color: COLORS.accentLight,
                fontWeight: 'bold',
              }}>{item.label}</span>
              {showDot && <Dot />}
            </React.Fragment>
          );
        }
        if (item.type === 'broad-specific') {
          return (
            <React.Fragment key={i}>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                <span style={{ color: COLORS.accentLight, fontWeight: 'bold' }}>{item.broad}</span>
                <span style={{ color: COLORS.textDim }}>: </span>
                {item.specifics.map((sp, j) => (
                  <span key={j}>
                    <span style={{ color: COLORS.textMuted }}>{sp}</span>
                    {j < item.specifics.length - 1 && <span style={{ color: COLORS.textDim }}>, </span>}
                  </span>
                ))}
              </span>
              {showDot && <Dot />}
            </React.Fragment>
          );
        }
        if (item.type === 'plain') {
          return (
            <React.Fragment key={i}>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>{item.label}</span>
              {showDot && <Dot />}
            </React.Fragment>
          );
        }
        return null;
      })}
    </div>
  );
};

// ── buildPhaseSummary — phase-aware summary builder ──
const buildPhaseSummary = (broadSelected, specificSelected, taxonomy) => {
  if (!broadSelected || broadSelected.length === 0) return [];
  return broadSelected.map(cat => {
    const tier2 = taxonomy ? (taxonomy[cat] || []) : [];
    const specifics = specificSelected ? specificSelected.filter(t => tier2.includes(t)) : [];
    if (specifics.length === 0) return { type: 'broad-only', label: cat };
    return { type: 'broad-specific', broad: cat, specifics };
  });
};

// ── buildColorSummary ──
const buildColorSummary = (colorStop) => {
  if (!colorStop) return [];
  return [{ type: 'plain', label: colorStop }];
};

// ── buildFinishSummary ──
const buildFinishSummary = (finishLength, finishBroad, finishSpecific, taxonomy) => {
  const items = [];
  if (finishLength) items.push({ type: 'plain', label: finishLength });
  const charItems = buildPhaseSummary(finishBroad, finishSpecific, taxonomy);
  return [...items, ...charItems];
};

export const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSendLink = async () => {
    if (!email) { setError('Please enter your email'); return; }
    setSending(true); setError('');
    const { error: authError } = await supabase.auth.signInWithOtp({
      email, options: { emailRedirectTo: 'https://barrelnotes.app' },
    });
    if (authError) {
      setError(authError.message.includes('rate limit')
        ? 'Please wait 60 seconds before requesting another link.' : authError.message);
    } else { setSent(true); }
    setSending(false);
  };

  return (
    <div style={{
      backgroundColor: COLORS.bg, minHeight: '100vh', maxWidth: 430,
      margin: '0 auto', fontFamily: 'Georgia, serif',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 30,
    }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🥃</div>
      <h1 style={{ color: COLORS.accent, letterSpacing: 3, fontSize: 26, margin: '0 0 6px 0' }}>BarrelNotes</h1>
      <p style={{ color: COLORS.textMuted, letterSpacing: 4, fontSize: 10, margin: '0 0 40px 0' }}>YOUR BOURBON JOURNAL</p>
      {sent ? (
        <div style={{ textAlign: 'center', width: '100%' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
          <p style={{ color: COLORS.green, fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>Check your email</p>
          <p style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.6 }}>We sent a login link to {email}. Tap it to sign in.</p>
          <p style={{ color: COLORS.textDim, fontSize: 11, marginTop: 16 }}>Link expires in 1 hour.</p>
          <button style={{ ...s.secondaryBtn, marginTop: 24 }} onClick={() => setSent(false)}>Use a different email</button>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          {error && <div style={s.errorBanner}>⚠️ {error}</div>}
          <label style={s.label}>Your Email</label>
          <input style={s.input} placeholder="you@example.com" type="email"
            value={email} onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendLink()} />
          <button style={s.primaryBtn} onClick={handleSendLink} disabled={sending}>
            {sending ? 'Sending...' : '✉️ Send Login Link'}
          </button>
          <p style={{ color: COLORS.textMuted, fontSize: 11, textAlign: 'center', marginTop: 8, lineHeight: 1.6 }}>
            No password needed. We'll email you a secure link to sign in.
          </p>
        </div>
      )}
    </div>
  );
};

export const HomeScreen = ({ bottles, scoreMap, recentTastings, totalNotes, onNavigate, onExport }) => {
  const open = bottles.filter(b => b.status === 'Open');
  const sealed = bottles.filter(b => b.status === 'Sealed');
  const total = open.length + sealed.length;
  const crownJewels = bottles.filter(b => b.is_crown_jewel).slice(0, 5);
  const bestPours = Object.entries(scoreMap)
  .map(([bottleId, scores]) => ({ bottleId, avgScore: Math.round(scores.total / scores.count) }))
  .sort((a, b) => b.avgScore - a.avgScore).slice(0, 5)
  .map(({ bottleId, avgScore }) => { 
    const bottle = bottles.find(b => String(b.id) === String(bottleId)); 
    return bottle ? { ...bottle, avgScore } : null; 
  })
  .filter(Boolean);

  const notesRemaining = 20 - totalNotes;
  const progressPct = Math.min((totalNotes / 20) * 100, 100);
  const unlocked = totalNotes >= 20;

  const CrownCard = ({ bottle }) => (
    <div onClick={() => onNavigate('detail', bottle)} style={{
      flexShrink: 0, width: 200,
      background: `linear-gradient(145deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
      border: `1px solid ${COLORS.accentDark}`,
      borderRadius: 14, padding: 18, cursor: 'pointer',
      boxShadow: `0 6px 24px rgba(0,0,0,0.7), inset 0 1px 0 ${COLORS.glowAmber}`,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${COLORS.accentLight}, transparent)`,
      }} />
      <p style={{ fontSize: 15, fontWeight: 'bold', color: COLORS.text, margin: '0 0 6px 0', lineHeight: 1.3 }}>
        {bottle.expression_name}
      </p>
      <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '0 0 12px 0' }}>
        {bottle.distillery}{bottle.proof ? ` · ${bottle.proof} proof` : ''}
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {bottle.personal_tier && (
          <span style={s.tag(bottle.personal_tier === 'Legendary' ? COLORS.accentLight : COLORS.accent)}>
            {bottle.personal_tier}
          </span>
        )}
        {getAvgScore(scoreMap, bottle.id) && (
          <span style={s.tag(getScoreColor(getAvgScore(scoreMap, bottle.id)))}>
            ⭐ {getAvgScore(scoreMap, bottle.id)}
          </span>
        )}
      </div>
    </div>
  );

  const PourCard = ({ bottle, avgScore }) => (
    <div onClick={() => onNavigate('detail', bottle)} style={{
      flexShrink: 0, width: 155,
      background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12, padding: 14, cursor: 'pointer',
      boxShadow: `0 4px 14px rgba(0,0,0,0.55)`,
    }}>
      <p style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.text, margin: '0 0 4px 0', lineHeight: 1.3 }}>
        {bottle.expression_name}
      </p>
      <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '0 0 10px 0' }}>
        {bottle.proof ? `${bottle.proof} proof` : bottle.distillery}
      </p>
      {avgScore && <span style={s.tag(getScoreColor(avgScore))}>⭐ {avgScore}</span>}
    </div>
  );

  return (
    <div style={s.screen}>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <div style={{
          flex: 1,
          background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14, padding: '16px 12px', textAlign: 'center',
          boxShadow: `0 4px 16px rgba(0,0,0,0.6)`,
        }}>
          <p style={{ fontSize: 32, fontWeight: 'bold', color: COLORS.accentLight, margin: 0, lineHeight: 1 }}>{total}</p>
          <span style={{ fontSize: 9, color: COLORS.textDim, letterSpacing: 3, textTransform: 'uppercase', marginTop: 5, display: 'block' }}>Total</span>
        </div>
        {[{ num: open.length, label: 'Open', filter: 'Open', color: COLORS.accent },
          { num: sealed.length, label: 'Sealed', filter: 'Sealed', color: COLORS.textMuted }].map(({ num, label, filter, color }) => (
          <div key={label} onClick={() => onNavigate('collection', { initialFilter: filter })} style={{
            flex: 1,
            background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
            border: `1px solid ${COLORS.accent}35`,
            borderRadius: 14, padding: '16px 12px', textAlign: 'center',
            cursor: 'pointer', boxShadow: `0 4px 16px rgba(0,0,0,0.6)`,
            position: 'relative',
          }}>
            <p style={{ fontSize: 32, fontWeight: 'bold', color, margin: 0, lineHeight: 1 }}>{num}</p>
            <span style={{ fontSize: 9, color: COLORS.textDim, letterSpacing: 3, textTransform: 'uppercase', marginTop: 5, display: 'block' }}>{label}</span>
            <span style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 10, color: COLORS.accentDark }}>›</span>
          </div>
        ))}
      </div>

      {/* Crown Jewels */}
      {crownJewels.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 16 }}>👑</span>
            <span style={{ fontSize: 13, color: COLORS.accentLight, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 'bold' }}>Crown Jewels</span>
          </div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
            {crownJewels.map((bottle, i) => <CrownCard key={i} bottle={bottle} />)}
            <div style={{ flexShrink: 0, width: 4 }} />
          </div>
        </div>
      )}

      {/* Best Pours */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 16 }}>⭐</span>
          <span style={{ fontSize: 13, color: COLORS.accentLight, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 'bold' }}>Best Pours</span>
        </div>
        {bestPours.length === 0 ? (
          <div style={{
            background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
            border: `1px solid ${COLORS.border}`, borderRadius: 12,
            padding: 20, textAlign: 'center',
          }}>
            <p style={{ color: COLORS.textDim, fontSize: 13, margin: 0 }}>Add tasting notes with ratings to see your best pours.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
            {bestPours.map((bottle, i) => <PourCard key={i} bottle={bottle} avgScore={bottle.avgScore} />)}
            <div style={{ flexShrink: 0, width: 4 }} />
          </div>
        )}
      </div>

      {/* Taste DNA */}
      {!unlocked ? (
        <div style={{
          background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14, padding: 18, marginBottom: 12,
          boxShadow: `0 4px 16px rgba(0,0,0,0.6)`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <p style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 3, margin: '0 0 4px 0', textTransform: 'uppercase' }}>🧬 Taste DNA</p>
              <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
                {notesRemaining === 1 ? '1 note to unlock' : `${notesRemaining} notes to unlock`}
              </p>
            </div>
            <span style={{ fontSize: 11, color: COLORS.textDim }}>{totalNotes} / 20</span>
          </div>
          <div style={{
            background: COLORS.bg, borderRadius: 6, height: 6,
            overflow: 'hidden', border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${COLORS.accentDark}, ${COLORS.accent}, ${COLORS.accentLight})`,
              borderRadius: 6, transition: 'width 0.6s ease',
            }} />
          </div>
        </div>
      ) : (
        <div style={{
          background: `linear-gradient(160deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`,
          border: `1px solid ${COLORS.accent}`,
          borderRadius: 14, padding: 18, marginBottom: 12,
          boxShadow: `0 4px 24px ${COLORS.glowAmber}`,
        }}>
          <p style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 3, margin: '0 0 4px 0', textTransform: 'uppercase' }}>🧬 Taste DNA</p>
          <p style={{ fontSize: 13, color: COLORS.accentLight, fontWeight: 'bold', margin: '0 0 10px 0' }}>Unlocked ✓</p>
          <button onClick={() => alert('Taste DNA coming soon.')} style={{
            width: '100%', padding: '10px', borderRadius: 8,
            border: `1px solid ${COLORS.accent}`,
            background: `linear-gradient(135deg, ${COLORS.accentLight}15, ${COLORS.accent}20)`,
            color: COLORS.accent, fontSize: 12, fontFamily: 'Georgia, serif',
            cursor: 'pointer', letterSpacing: 1,
          }}>VIEW TASTE DNA →</button>
        </div>
      )}

      {/* Export */}
      <div style={{ marginTop: 4, marginBottom: 4 }}>
        <button onClick={onExport} style={{
          width: '100%', padding: '12px', borderRadius: 10,
          border: `1px solid ${COLORS.border}`, background: 'transparent',
          color: COLORS.textDim, fontSize: 12, fontFamily: 'Georgia, serif',
          cursor: 'pointer', letterSpacing: 1,
        }}>⬇️ Export My Data</button>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
};

export const CollectionScreen = ({
  bottles, scoreMap, onNavigate,
  initialFilter = 'All', initialTypeFilter = 'All',
  initialSort = 'name', initialSortDir = 'asc',
  initialMode = 'collection', onStateChange,
}) => {
  const [mode, setMode] = useState(initialMode);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(initialFilter);
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter);
  const [sort, setSort] = useState(initialSort);
  const [sortDir, setSortDir] = useState(initialSortDir);
  const listRef = useRef(null);
  const isArchive = mode === 'archive';
  const statusFilters = isArchive ? ['All', 'Emptied', 'Gifted'] : ['All', 'Open', 'Sealed'];

  const handleModeSwitch = (newMode) => { setMode(newMode); setFilter('All'); setSearch(''); };
  useEffect(() => { if (onStateChange) onStateChange({ filter, typeFilter, sort, sortDir, mode }); }, [filter, typeFilter, sort, sortDir, mode]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = 0; }, [filter, typeFilter, sort, sortDir, search, mode]);

  const handleSort = (newSort) => {
    if (sort === newSort) { setSortDir(prev => prev === 'asc' ? 'desc' : 'asc'); }
    else { setSort(newSort); setSortDir(newSort === 'risk' || newSort === 'score' || newSort === 'tier' ? 'desc' : 'asc'); }
  };
  const getSortIcon = (sortKey) => sort !== sortKey ? '' : sortDir === 'asc' ? ' ↑' : ' ↓';

  const filtered = bottles.filter(b => {
    const matchSearch = b.expression_name?.toLowerCase().includes(search.toLowerCase()) ||
      b.distillery?.toLowerCase().includes(search.toLowerCase()) || b.nickname?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All'
      ? (isArchive ? (b.status === 'Emptied' || b.status === 'Gifted') : (b.status === 'Open' || b.status === 'Sealed'))
      : b.status === filter;
    return matchSearch && matchFilter && (typeFilter === 'All' || b.type === typeFilter);
  }).sort((a, b) => {
    let comparison = 0;
    if (sort === 'name') comparison = (a.expression_name || '').localeCompare(b.expression_name || '');
    if (sort === 'proof') comparison = (a.proof || 0) - (b.proof || 0);
    if (sort === 'fill') comparison = (a.fill_level || 0) - (b.fill_level || 0);
    if (sort === 'risk') comparison = (calculateOxidationRisk(a) || 0) - (calculateOxidationRisk(b) || 0);
    if (sort === 'score') comparison = (getAvgScore(scoreMap, a.id) || 0) - (getAvgScore(scoreMap, b.id) || 0);
    if (sort === 'tier') { const tv = t => t === 'Legendary' ? 4 : t === 'Premium' ? 3 : t === 'Standard' ? 2 : t === 'Pass' ? 1 : 0; comparison = tv(a.personal_tier) - tv(b.personal_tier); }
    return sortDir === 'asc' ? comparison : -comparison;
  });

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, minHeight: 30 };
  const labelStyle = { fontSize: 10, color: COLORS.textMuted, letterSpacing: 2, textTransform: 'uppercase', flexShrink: 0, width: 44 };
  const scrollRowStyle = { display: 'flex', gap: 6, overflowX: 'auto', flex: 1, scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: 2 };

  const ModeToggle = () => (
    <div onClick={() => handleModeSwitch(isArchive ? 'collection' : 'archive')}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', flexShrink: 0, gap: 2 }}>
      <span style={{ fontSize: 8, color: isArchive ? COLORS.textDim : COLORS.accent, letterSpacing: 0.5, fontFamily: 'Georgia, serif', fontWeight: isArchive ? 'normal' : 'bold', transition: 'color 0.2s' }}>Active</span>
      <div style={{ width: 32, height: 16, borderRadius: 8, backgroundColor: isArchive ? COLORS.borderStrong : COLORS.accent, position: 'relative', transition: 'background-color 0.2s', flexShrink: 0, border: `1px solid ${isArchive ? COLORS.textDim : COLORS.accentDark}` }}>
        <div style={{ width: 11, height: 11, borderRadius: '50%', backgroundColor: isArchive ? COLORS.textMuted : COLORS.bg, position: 'absolute', top: 2, left: isArchive ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
      </div>
      <span style={{ fontSize: 8, color: isArchive ? COLORS.textMuted : COLORS.textDim, letterSpacing: 0.5, fontFamily: 'Georgia, serif', fontWeight: isArchive ? 'bold' : 'normal', transition: 'color 0.2s' }}>Archive</span>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, padding: '10px 16px 8px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <input style={{ ...s.input, marginBottom: 0, flex: 1 }} placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} />
          {!isArchive && <button style={s.addBtn} onClick={() => onNavigate('add-bottle', { initialName: search })}>+ Add</button>}
          <ModeToggle />
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Status</span>
          <div style={scrollRowStyle}>{statusFilters.map(f => <button key={f} style={{ ...s.filterChip(filter === f), flexShrink: 0 }} onClick={() => setFilter(f)}>{f}</button>)}</div>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Type</span>
          <div style={scrollRowStyle}>
            <button style={{ ...s.filterChip(typeFilter === 'All'), flexShrink: 0 }} onClick={() => setTypeFilter('All')}>All</button>
            {WHISKEY_TYPES.map(t => <button key={t} style={{ ...s.filterChip(typeFilter === t), flexShrink: 0 }} onClick={() => setTypeFilter(typeFilter === t ? 'All' : t)}>{t}</button>)}
          </div>
        </div>
        <div style={{ ...rowStyle, marginBottom: 0 }}>
          <span style={labelStyle}>Sort</span>
          <div style={scrollRowStyle}>
            {[['name','Name'],['proof','Proof'],['fill','Fill'],['risk','Risk'],['score','Score'],['tier','Tier']].map(([val, label]) => (
              <button key={val} style={{ ...s.filterChip(sort === val), flexShrink: 0 }} onClick={() => handleSort(val)}>{label}{getSortIcon(val)}</button>
            ))}
          </div>
        </div>
      </div>
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 80px' }}>
        <div style={{ ...s.sectionTitle, color: isArchive ? COLORS.textDim : COLORS.textMuted }}>
          {isArchive ? `📦 Archive — ${filtered.length} Bottles` : `🥃 Collection — ${filtered.length} Bottles`}
        </div>
        {filtered.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{isArchive ? '📦' : '🥃'}</div>
            <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>
              {isArchive ? "Bottles you've finished or gifted will appear here." : 'No bottles match your search.'}
            </p>
          </div>
        )}
        {filtered.map((bottle, i) => (
          <div key={i} style={{ opacity: isArchive ? 0.85 : 1 }}>
            <BottleCard bottle={bottle} avgScore={getAvgScore(scoreMap, bottle.id)} onClick={() => onNavigate('detail', bottle)} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const AddBottleScreen = ({ bottle, initialName = '', onNavigate, onSave }) => {
  const isEdit = !!bottle;
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupSuccess, setLookupSuccess] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPreservationInfo, setShowPreservationInfo] = useState(false);
  const [form, setForm] = useState({
    expression_name: bottle?.expression_name || initialName || '',
    nickname: bottle?.nickname || '', distillery: bottle?.distillery || '',
    producer: bottle?.producer || '', type: bottle?.type || 'Bourbon',
    mash_bill: bottle?.mash_bill || '', age_statement: bottle?.age_statement || '',
    proof: bottle?.proof || '', batch_lot_code: bottle?.batch_lot_code || '',
    status: bottle?.status || 'Sealed', fill_level: bottle?.fill_level || 100,
    date_opened: bottle?.date_opened || '', store_source: bottle?.store_source || '',
    season_acquired: bottle?.season_acquired || '', year_acquired: bottle?.year_acquired || '',
    purchase_price: bottle?.purchase_price || '', personal_tier: bottle?.personal_tier || '',
    limited_allocated: bottle?.limited_allocated || false, single_barrel: bottle?.single_barrel || false,
    store_pick: bottle?.store_pick || false, is_finished: bottle?.is_finished || false,
    finish_cask: bottle?.finish_cask || '', finish_duration: bottle?.finish_duration || '',
    preservation_method: bottle?.preservation_method || 'none', notes: bottle?.notes || '',
    volume_ml: bottle?.volume_ml || 750,
  });
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleStatusChange = (val) => {
    set('status', val);
    if (val === 'Open' && !form.date_opened) set('date_opened', new Date().toISOString().split('T')[0]);
  };

  const handleLookup = async () => {
    if (!form.expression_name) { setLookupError('Enter a bottle name first'); return; }
    setLooking(true); setLookupError(''); setLookupSuccess('');
    try {
      const result = await lookupBottle(form.expression_name);
      setForm(prev => ({
        ...prev,
        expression_name: result.expression_name || prev.expression_name,
        distillery: result.distillery || prev.distillery,
        producer: result.producer || prev.producer,
        type: result.type || prev.type,
        proof: result.proof ? String(result.proof) : prev.proof,
        age_statement: result.age_statement || prev.age_statement,
        mash_bill: result.mash_bill || prev.mash_bill,
        limited_allocated: result.limited_allocated !== undefined ? result.limited_allocated : prev.limited_allocated,
        single_barrel: result.single_barrel !== undefined ? result.single_barrel : prev.single_barrel,
      }));
      setLookupSuccess('✅ Found it! Review the fields below and correct anything if needed.');
    } catch (err) { setLookupError("Couldn't find that one automatically. No problem — fill in the details manually below."); }
    setLooking(false);
  };

  const handleSave = async () => {
    if (!form.expression_name) { setError('Bottle name is required'); return; }
    setSaving(true); setError('');
    try {
      const newFillLevel = form.status === 'Open' ? parseInt(form.fill_level) : 100;
      const record = {
        ...form, proof: form.proof ? parseFloat(form.proof) : null,
        fill_level: newFillLevel,
        year_acquired: form.year_acquired ? parseInt(form.year_acquired) : null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        date_opened: form.date_opened || null,
        volume_ml: form.volume_ml ? parseInt(form.volume_ml) : 750,
      };
      if (isEdit) {
        await db.update('bottles', bottle.id, record);
        if (form.status === 'Open' && newFillLevel !== bottle.fill_level) {
          await db.insert('fill_level_history', { bottle_id: bottle.id, fill_level: newFillLevel, recorded_at: new Date().toISOString() });
        }
      } else { await db.insert('bottles', record); }
      setSuccess(true);
      setTimeout(() => { onSave(); onNavigate('collection', null); }, 1000);
    } catch (err) { setError('Failed to save. Please try again.'); }
    setSaving(false);
  };

  return (
    <div style={s.screen}>
      {looking && (
        <div style={s.lookupOverlay}>
          <div style={{ fontSize: 48 }}>🔍</div>
          <p style={{ color: COLORS.accent, fontSize: 16, fontWeight: 'bold', letterSpacing: 2, textAlign: 'center' }}>Looking Up Bottle...</p>
          <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>Searching the web for distillery, mash bill, proof and more</p>
        </div>
      )}
      <button style={s.backBtn} onClick={() => onNavigate(isEdit ? 'detail' : 'collection', bottle)}>← Back</button>
      <h2 style={{ color: COLORS.text, margin: '0 0 16px 0', fontSize: 18 }}>{isEdit ? 'Edit Bottle' : 'Add New Bottle'}</h2>
      {success && <div style={s.successBanner}>✅ Bottle saved successfully!</div>}
      {error && <div style={s.errorBanner}>⚠️ {error}</div>}
      <div style={s.card}>
        <p style={s.cardTitle}>IDENTITY</p>
        <label style={s.label}>Bottle Name *</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...s.input, marginBottom: 0, flex: 1 }} placeholder="e.g. Eagle Rare 10 Year"
            value={form.expression_name} onChange={e => set('expression_name', e.target.value)} />
          <button style={{ background: `linear-gradient(135deg, ${COLORS.accentLight} 0%, ${COLORS.accent} 100%)`, color: COLORS.bg, border: 'none', borderRadius: 8, padding: '0 14px', fontFamily: 'Georgia, serif', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', opacity: looking ? 0.6 : 1 }}
            onClick={handleLookup} disabled={looking}>🔍 Look Up</button>
        </div>
        {lookupSuccess && <div style={{ ...s.successBanner, marginBottom: 12 }}>{lookupSuccess}</div>}
        {lookupError && <div style={{ ...s.errorBanner, marginBottom: 12 }}>⚠️ {lookupError}</div>}
        <label style={s.label}>Nickname (Optional)</label>
        <input style={s.input} placeholder="e.g. Christmas Gift 2024" value={form.nickname} onChange={e => set('nickname', e.target.value)} />
        <label style={s.label}>Distillery</label>
        <input style={s.input} placeholder="e.g. Buffalo Trace" value={form.distillery} onChange={e => set('distillery', e.target.value)} />
        <label style={s.label}>Type</label>
        <select style={s.select} value={form.type} onChange={e => set('type', e.target.value)}>
          {WHISKEY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div style={s.card}>
        <p style={s.cardTitle}>DETAILS</p>
        <label style={s.label}>Proof</label>
        <input style={s.input} placeholder="e.g. 90" type="number" value={form.proof} onChange={e => set('proof', e.target.value)} />
        <label style={s.label}>Age Statement</label>
        <input style={s.input} placeholder="e.g. 10 or NAS" value={form.age_statement} onChange={e => set('age_statement', e.target.value)} />
        <label style={s.label}>Mash Bill</label>
        <input style={s.input} placeholder="e.g. 75% corn, 13% rye, 12% malt" value={form.mash_bill} onChange={e => set('mash_bill', e.target.value)} />
        <label style={s.label}>Batch / Lot Code</label>
        <input style={s.input} placeholder="e.g. C922 or 2025-02" value={form.batch_lot_code} onChange={e => set('batch_lot_code', e.target.value)} />
        <label style={s.label}>Bottle Volume (mL)</label>
        <input style={s.input} type="number" placeholder="750" value={form.volume_ml} onChange={e => set('volume_ml', parseInt(e.target.value) || 750)} />
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          {[['limited_allocated','Limited'],['single_barrel','Single Barrel'],['store_pick','Store Pick']].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLORS.textMuted, cursor: 'pointer' }}>
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} style={{ accentColor: COLORS.accent }} />{label}
            </label>
          ))}
        </div>
        <div style={s.divider} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
          <input type="checkbox" checked={form.is_finished} onChange={e => set('is_finished', e.target.checked)} style={{ accentColor: FINISHED_COLOR }} />
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>Finished Whiskey (secondary cask aging)</span>
        </label>
        {form.is_finished && (
          <>
            <label style={s.label}>Finishing Cask</label>
            <select style={s.select} value={form.finish_cask} onChange={e => set('finish_cask', e.target.value)}>
              <option value="">Select cask type...</option>
              {FINISH_CASKS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <label style={s.label}>Finish Duration (optional)</label>
            <input style={s.input} placeholder="e.g. 6 months, 1 year, undisclosed" value={form.finish_duration} onChange={e => set('finish_duration', e.target.value)} />
          </>
        )}
      </div>
      <div style={s.card}>
        <p style={s.cardTitle}>STATUS & PROVENANCE</p>
        <label style={s.label}>Status</label>
        <select style={s.select} value={form.status} onChange={e => handleStatusChange(e.target.value)}>
          {['Sealed','Open','Emptied','Gifted'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {form.status === 'Open' && (
          <>
            <label style={s.label}>Fill Level — {form.fill_level}% (~{getPourCount(form.fill_level, form.volume_ml)} pours remaining)</label>
            <input type="range" min={0} max={100} step={10} value={form.fill_level} onChange={e => set('fill_level', e.target.value)} style={{ width: '100%', accentColor: COLORS.accent, marginBottom: 12 }} />
            <label style={s.label}>Date Opened</label>
            <input style={s.input} type="date" value={form.date_opened} onChange={e => set('date_opened', e.target.value)} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ ...s.label, marginBottom: 0 }}>Preservation Method</label>
              <button onClick={() => setShowPreservationInfo(!showPreservationInfo)} style={{ background: 'none', border: 'none', color: COLORS.accent, cursor: 'pointer', fontSize: 16 }}>ℹ️</button>
            </div>
            {showPreservationInfo && (
              <div style={s.infoBox}>
                {PRESERVATION_METHODS.filter(m => m.value !== 'none').map(m => (
                  <div key={m.value} style={{ marginBottom: 8 }}><span style={{ color: COLORS.accent, fontWeight: 'bold' }}>{m.label}:</span> {m.description}</div>
                ))}
              </div>
            )}
            {PRESERVATION_METHODS.map(method => (
              <label key={method.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
                <input type="radio" name="preservation" value={method.value} checked={form.preservation_method === method.value} onChange={() => set('preservation_method', method.value)} style={{ accentColor: COLORS.accent }} />
                <span style={{ fontSize: 13, color: COLORS.text }}>{method.label}</span>
              </label>
            ))}
          </>
        )}
        <label style={s.label}>Store / Source</label>
        <input style={s.input} placeholder="e.g. Total Wine or Gift from Dave" value={form.store_source} onChange={e => set('store_source', e.target.value)} />
        <label style={s.label}>Season Acquired</label>
        <select style={s.select} value={form.season_acquired} onChange={e => set('season_acquired', e.target.value)}>
          <option value="">Unknown</option>
          {['Spring','Summer','Fall','Winter'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <label style={s.label}>Year Acquired</label>
        <input style={s.input} placeholder="e.g. 2024" type="number" value={form.year_acquired} onChange={e => set('year_acquired', e.target.value)} />
        <label style={s.label}>Purchase Price ($)</label>
        <input style={s.input} placeholder="e.g. 65" type="number" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} />
        <label style={s.label}>Personal Tier</label>
        <select style={s.select} value={form.personal_tier} onChange={e => set('personal_tier', e.target.value)}>
          <option value="">Not Rated</option>
          {['Legendary','Premium','Standard','Pass'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div style={s.card}>
        <p style={s.cardTitle}>NOTES</p>
        <textarea style={s.textarea} placeholder="Any personal notes about this bottle..." value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>
      <button style={s.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add To Collection'}</button>
      {isEdit && <button style={s.dangerBtn} onClick={() => onNavigate('detail', bottle)}>Cancel</button>}
      <div style={{ height: 20 }} />
    </div>
  );
};

export const UpdateFillScreen = ({ bottle, onNavigate, onSave }) => {
  const [fillLevel, setFillLevel] = useState(bottle?.fill_level || 100);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const newStatus = fillLevel === 0 ? 'Emptied' : 'Open';
      await db.update('bottles', bottle.id, { fill_level: fillLevel, status: newStatus, date_finished: fillLevel === 0 ? new Date().toISOString().split('T')[0] : null });
      await db.insert('fill_level_history', { bottle_id: bottle.id, fill_level: fillLevel, recorded_at: new Date().toISOString() });
      setSuccess(true);
      if (onSave) onSave();
      setTimeout(() => onNavigate('detail', { ...bottle, fill_level: fillLevel, status: newStatus }), 1000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={s.screen}>
      <button style={s.backBtn} onClick={() => onNavigate('detail', bottle)}>← Back</button>
      <h2 style={{ color: COLORS.text, margin: '0 0 4px 0', fontSize: 18 }}>Update Fill Level</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>{bottle?.expression_name}</p>
      {success && <div style={s.successBanner}>✅ Fill level updated!</div>}
      <div style={s.card}>
        <p style={s.cardTitle}>CURRENT FILL LEVEL</p>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 48, fontWeight: 'bold', color: getFillColor(fillLevel) }}>{fillLevel}%</span>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '4px 0 0 0' }}>~{getPourCount(fillLevel, bottle?.volume_ml)} pours remaining</p>
        </div>
        <input type="range" min={0} max={100} step={10} value={fillLevel} onChange={e => setFillLevel(parseInt(e.target.value))} style={{ width: '100%', accentColor: COLORS.accent, marginBottom: 8 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: COLORS.red }}>Empty</span>
          <span style={{ fontSize: 11, color: COLORS.green }}>Full</span>
        </div>
        {fillLevel === 0 && <div style={{ ...s.warningBanner, marginTop: 12 }}>⚠️ Setting to 0% will mark this bottle as Emptied.</div>}
        <div style={{ marginTop: 16 }}>
          <div style={s.fillBarBg}>
            <div style={{ height: '100%', width: `${fillLevel}%`, background: `linear-gradient(90deg, ${COLORS.accentDark}, ${COLORS.accent}, ${COLORS.accentLight})`, borderRadius: 3, transition: 'width 0.2s ease' }} />
          </div>
        </div>
      </div>
      <div style={s.card}>
        <p style={s.cardTitle}>QUICK SET</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[100,90,80,70,60,50,40,30,20,10,0].map(val => (
            <button key={val} style={{ ...s.filterChip(fillLevel === val), padding: '8px 14px' }} onClick={() => setFillLevel(val)}>
              {val === 0 ? 'Empty' : `${val}%`}
            </button>
          ))}
        </div>
      </div>
      <button style={s.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Update Fill Level'}</button>
    </div>
  );
};

export const DetailScreen = ({ bottle: initialBottle, onNavigate, onDelete, onSave, onUpdateBottle }) => {
  const [bottle, setBottle] = useState(initialBottle);
  const [tastingNotes, setTastingNotes] = useState([]);
  const [fillHistory, setFillHistory] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [expandedNote, setExpandedNote] = useState(null);
  const [showDeleteBottleModal, setShowDeleteBottleModal] = useState(false);
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    if (bottle?.id) {
      loadNotes();
      db.fetch('fill_level_history', '*', { bottle_id: bottle.id }).then(data => { if (Array.isArray(data)) setFillHistory(data); });
    }
  }, [bottle?.id]);

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      const data = await db.fetch('tasting_notes', '*', { bottle_id: bottle.id });
      if (Array.isArray(data)) {
        const sorted = data.sort((a, b) => new Date(b.tasting_date) - new Date(a.tasting_date));
        const notesWithFlavors = await Promise.all(sorted.map(async note => {
          const flavors = await db.fetch('flavor_selections', 'flavor_tag,phase,flavor_category', { bottle_id: bottle.id, tasting_date: note.tasting_date });
          return { ...note, flavors: Array.isArray(flavors) ? flavors : [] };
        }));
        setTastingNotes(notesWithFlavors);
      }
    } catch (err) { console.error(err); }
    setLoadingNotes(false);
  };

  const handleDeleteBottle = async (choice) => {
    try {
      if (choice === 'keep') { for (const note of tastingNotes) await db.update('tasting_notes', note.id, { bottle_id: null, is_experience: true }); }
      else { await db.delete('flavor_selections', 'bottle_id', bottle.id); await db.delete('tasting_notes', 'bottle_id', bottle.id); }
      await db.delete('fill_level_history', 'bottle_id', bottle.id);
      await db.delete('bottles', 'id', bottle.id);
      if (onDelete) onDelete();
      onNavigate('collection', null);
    } catch (err) { console.error(err); }
    setShowDeleteBottleModal(false);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    try {
      const { error } = await supabase.from('flavor_selections').delete().eq('bottle_id', bottle.id).eq('tasting_date', noteToDelete.tasting_date);
      if (error) throw error;
      await db.delete('tasting_notes', 'id', noteToDelete.id);
      setShowDeleteNoteModal(false); setNoteToDelete(null); loadNotes();
    } catch (err) { console.error(err); }
  };

  const handleToggleCrownJewel = async () => {
    const newVal = !bottle.is_crown_jewel;
    try {
      await db.update('bottles', bottle.id, { is_crown_jewel: newVal });
      const updatedBottle = { ...bottle, is_crown_jewel: newVal };
      setBottle(updatedBottle);
      if (onUpdateBottle) onUpdateBottle(updatedBottle);
    } catch (err) { console.error('Crown jewel update failed:', err); }
  };

  if (!bottle) return null;
  const oxidationScore = calculateOxidationRisk(bottle, fillHistory);
  const riskLevel = getRiskLevel(oxidationScore);
  const predictedFinish = getPredictedFinishDate(bottle, fillHistory);
  const proofLabel = getProofAdjustmentLabel(bottle.proof);
  const preservationMethod = bottle.preservation_method || 'none';

  return (
    <div style={s.screen}>
      {showDeleteBottleModal && <DeleteBottleModal bottle={bottle} tastingNoteCount={tastingNotes.length} onConfirm={handleDeleteBottle} onCancel={() => setShowDeleteBottleModal(false)} />}
      {showDeleteNoteModal && <DeleteNoteModal onConfirm={handleDeleteNote} onCancel={() => { setShowDeleteNoteModal(false); setNoteToDelete(null); }} />}
      <button style={s.backBtn} onClick={() => onNavigate('collection', null)}>← Back</button>
      <div style={{ ...s.card, textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🥃</div>
        <h2 style={{ color: COLORS.text, margin: '0 0 4px 0', fontSize: 20 }}>{bottle.expression_name}</h2>
        <p style={{ color: COLORS.textMuted, margin: '0 0 12px 0', fontSize: 13 }}>{bottle.distillery}</p>
        {bottle.nickname && <p style={{ color: COLORS.accent, fontStyle: 'italic', margin: '0 0 12px 0' }}>"{bottle.nickname}"</p>}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={s.tag()}>{bottle.proof} proof</span>
          <span style={s.tag(bottle.status === 'Sealed' ? COLORS.green : COLORS.accent)}>{bottle.status}</span>
          {bottle.personal_tier && <span style={s.tag(bottle.personal_tier === 'Legendary' ? COLORS.accentLight : COLORS.accent)}>{bottle.personal_tier}</span>}
          {bottle.limited_allocated && <span style={s.tag(COLORS.red)}>Allocated</span>}
          {bottle.is_finished && <span style={s.tag(FINISHED_COLOR)}>Finished — {bottle.finish_cask}</span>}
          {bottle.is_crown_jewel && <span style={s.tag(COLORS.accentLight)}>👑 Crown Jewel</span>}
        </div>
      </div>
      {bottle.status === 'Open' && oxidationScore !== null && riskLevel && (
        <div style={s.riskCard(riskLevel.color)}>
          <div>
            <div style={{ fontSize: 12, color: riskLevel.color, fontWeight: 'bold', marginBottom: 4 }}>{riskLevel.label} Oxidation Risk</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted }}>Score: {oxidationScore}/100{predictedFinish && ` • Est. finish: ${predictedFinish}`}</div>
            {proofLabel && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>🔬 {proofLabel}</div>}
            {preservationMethod !== 'none' && <div style={{ fontSize: 10, color: COLORS.green, marginTop: 3 }}>🛡️ {getPreservationLabel(preservationMethod)} active</div>}
          </div>
          <div style={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: riskLevel.color + '22', border: `2px solid ${riskLevel.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 'bold', color: riskLevel.color }}>{oxidationScore}</span>
          </div>
        </div>
      )}
      <div style={s.card}>
        <p style={s.cardTitle}>BOTTLE DETAILS</p>
        {[['Type',bottle.type],['Mash Bill',bottle.mash_bill||'Undisclosed'],['Age Statement',bottle.age_statement||'NAS'],['Proof',bottle.proof],['Volume',bottle.volume_ml?`${bottle.volume_ml}mL`:'750mL'],['Batch / Lot',bottle.batch_lot_code||'—'],['Store / Source',bottle.store_source||'—'],['Season Acquired',bottle.season_acquired||'—'],['Year Acquired',bottle.year_acquired||'—'],['Purchase Price',bottle.purchase_price?`$${bottle.purchase_price}`:'—'],['Date Opened',bottle.date_opened?formatDate(bottle.date_opened):'—'],['Single Barrel',bottle.single_barrel?'Yes':'No'],['Store Pick',bottle.store_pick?'Yes':'No'],['Limited / Allocated',bottle.limited_allocated?'Yes':'No']].map(([label, value]) => (
          <div key={label} style={s.detailRow}><span style={{ color: COLORS.textMuted }}>{label}</span><span style={{ color: COLORS.text }}>{value}</span></div>
        ))}
        {bottle.is_finished && (<><div style={s.divider} /><div style={s.detailRow}><span style={{ color: COLORS.textMuted }}>Finishing Cask</span><span style={{ color: FINISHED_COLOR }}>{bottle.finish_cask||'—'}</span></div>{bottle.finish_duration&&<div style={s.detailRow}><span style={{ color: COLORS.textMuted }}>Finish Duration</span><span style={{ color: COLORS.text }}>{bottle.finish_duration}</span></div>}</>)}
        {bottle.status === 'Open' && bottle.fill_level !== undefined && (<><div style={s.divider} /><p style={{ ...s.cardTitle, margin: '0 0 6px 0' }}>Fill Level — {bottle.fill_level}% (~{getPourCount(bottle.fill_level, bottle.volume_ml)} pours)</p><div style={s.fillBarBg}><div style={{ height: '100%', width: `${bottle.fill_level}%`, background: `linear-gradient(90deg, ${COLORS.accentDark}, ${COLORS.accent}, ${COLORS.accentLight})`, borderRadius: 3 }} /></div>{preservationMethod !== 'none' && <p style={{ fontSize: 11, color: COLORS.green, marginTop: 6 }}>🛡️ {getPreservationLabel(preservationMethod)}</p>}</>)}
        {bottle.notes && (<><div style={s.divider} /><p style={{ ...s.cardTitle, margin: '0 0 6px 0' }}>NOTES</p><p style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', margin: 0 }}>{bottle.notes}</p></>)}
      </div>
      <button style={s.primaryBtn} onClick={() => onNavigate('tasting', bottle)}>+ Add Tasting Note</button>
      <button style={{ ...s.secondaryBtn, borderColor: bottle.is_crown_jewel ? COLORS.accentLight : COLORS.border, color: bottle.is_crown_jewel ? COLORS.accentLight : COLORS.textMuted }} onClick={handleToggleCrownJewel}>
        {bottle.is_crown_jewel ? '👑 Crown Jewel — Tap to Remove' : '👑 Add to Crown Jewels'}
      </button>
      {bottle.status === 'Open' && <button style={s.secondaryBtn} onClick={() => onNavigate('update-fill', bottle)}>📊 Update Fill Level</button>}
      <button style={s.secondaryBtn} onClick={() => onNavigate('edit-bottle', bottle)}>✏️ Edit Bottle Details</button>
      <button style={s.dangerBtn} onClick={() => setShowDeleteBottleModal(true)}>🗑️ Delete Bottle</button>
      <div style={{ ...s.sectionTitle, marginTop: 8 }}>Tasting History — {tastingNotes.length} Note{tastingNotes.length !== 1 ? 's' : ''}</div>
      {loadingNotes && <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted, fontSize: 13 }}>Loading tasting notes...</div>}
      {!loadingNotes && tastingNotes.length === 0 && <div style={{ ...s.card, textAlign: 'center', padding: 24 }}><div style={{ fontSize: 32, marginBottom: 8 }}>📝</div><p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>No tasting notes yet.</p></div>}

      {tastingNotes.map((note, i) => {
        // ── Build phase-aware flavor structures for MiniSummary ──
        const getPhase = (phase, cat) =>
          note.flavors?.filter(f => f.phase === phase && (f.flavor_category === cat || f.flavor_category === 'General')).map(f => f.flavor_tag) || [];
        const getPhaseExact = (phase, cat) =>
          note.flavors?.filter(f => f.phase === phase && f.flavor_category === cat).map(f => f.flavor_tag) || [];

        const noseBroad     = getPhase('nose', 'broad');
        const noseSpecific  = getPhaseExact('nose', 'specific');
        const palateBroad   = getPhase('palate', 'broad');
        const palateSpecific = getPhaseExact('palate', 'specific');
        const finishBroad   = getPhase('finish', 'broad');
        const finishSpecific = getPhaseExact('finish', 'specific');
        const finishLengthTag = getPhaseExact('finish', 'length')[0] || null;

        const noseSummary   = buildPhaseSummary(noseBroad, noseSpecific, NOSE_FLAVORS);
        const palateSummary = buildPhaseSummary(palateBroad, palateSpecific, PALATE_FLAVORS);
        const finishSummary = buildFinishSummary(finishLengthTag, finishBroad, finishSpecific, FINISH_FLAVORS);

        // Legacy fallback — old notes with no phase data
        const allFlavors = note.flavors?.map(f => f.flavor_tag) || [];
        const hasPhaseData = noseBroad.length > 0 || palateBroad.length > 0 || finishBroad.length > 0;

        return (
          <div key={note.id || i} style={s.tastingNoteCard}>
            <div style={s.tastingNoteHeader} onClick={() => setExpandedNote(expandedNote === i ? null : i)}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.text }}>{formatDate(note.tasting_date)}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{note.serving_style && `${note.serving_style} • `}Fill: {note.fill_level_at_tasting}%</div>
                {note.color_stop && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>🎨 {note.color_stop}</div>}
                {note.occasion_notes && <div style={{ fontSize: 11, color: COLORS.accent, fontStyle: 'italic', marginTop: 2 }}>{note.occasion_notes}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {note.overall_score && <div style={{ ...s.scoreCircle, backgroundColor: getScoreColor(note.overall_score) + '22', border: `2px solid ${getScoreColor(note.overall_score)}` }}><span style={{ fontSize: 14, fontWeight: 'bold', color: getScoreColor(note.overall_score), lineHeight: 1 }}>{note.overall_score}</span><span style={{ fontSize: 7, color: COLORS.textMuted, letterSpacing: 1 }}>SCORE</span></div>}
                {note.would_reach_again !== null && note.would_reach_again !== undefined && <span style={{ fontSize: 20 }}>{note.would_reach_again ? '👍' : '👎'}</span>}
                <span style={{ color: COLORS.textMuted, fontSize: 16 }}>{expandedNote === i ? '▲' : '▼'}</span>
              </div>
            </div>

            {expandedNote === i && (
              <div>
                <div style={s.divider} />

                {/* Color */}
                {note.color_stop && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>COLOR</div>
                    <span style={s.tag(COLORS.textMuted)}>{note.color_stop}</span>
                    {note.color_notes && <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 6, lineHeight: 1.6 }}>{note.color_notes}</div>}
                  </div>
                )}

                {/* Nose — MiniSummary */}
                {noseSummary.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>NOSE</div>
                    <MiniSummary summary={noseSummary} />
                    {note.nose_intensity && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Intensity: {note.nose_intensity}</div>}
                  </div>
                )}

                {/* Palate — MiniSummary */}
                {palateSummary.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>PALATE</div>
                    <MiniSummary summary={palateSummary} />
                    {note.mouthfeel_text && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Mouthfeel: {note.mouthfeel_text}</div>}
                    {note.heat && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Heat: {note.heat}</div>}
                  </div>
                )}

                {/* Finish — MiniSummary */}
                {finishSummary.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>FINISH</div>
                    <MiniSummary summary={finishSummary} />
                    {note.finish_fade && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Fade: {note.finish_fade}</div>}
                  </div>
                )}

                {/* Legacy fallback */}
                {!hasPhaseData && allFlavors.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>FLAVORS</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {allFlavors.map(f => <span key={f} style={s.tag()}>{f}</span>)}
                    </div>
                  </div>
                )}

                {/* Impressions */}
                {note.reason_tags?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>IMPRESSIONS</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {note.reason_tags.map(t => <span key={t} style={s.tag(COLORS.textMuted)}>{t}</span>)}
                    </div>
                  </div>
                )}

                {/* Free text notes */}
                {[['Nose', note.nose_notes], ['Palate', note.palate_notes], ['Finish', note.finish_notes], ['Overall', note.overall_notes]].filter(([, text]) => text).map(([label, text]) => (
                  <div key={label} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{text}</div>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button style={{ ...s.secondaryBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0, flex: 1 }} onClick={() => onNavigate('edit-tasting', { note, bottle })}>✏️ Edit</button>
                  <button style={{ ...s.dangerBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0, flex: 1 }} onClick={() => { setNoteToDelete(note); setShowDeleteNoteModal(true); }}>🗑️ Delete</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ height: 20 }} />
    </div>
  );
};

// ── TastingChoiceScreen ───────────────────────────────────────────────────────
const TastingChoiceScreen = ({ bottles, scoreMap, recentTastings = [], onNavigate, onChooseCollection, onChooseBar }) => {
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);

  const eligible = bottles.filter(b => b.status === 'Open' || b.status === 'Sealed').filter(b => !search ||
    b.expression_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.distillery?.toLowerCase().includes(search.toLowerCase()) ||
    b.nickname?.toLowerCase().includes(search.toLowerCase()));

  if (mode === 'collection') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, padding: '10px 16px 10px', flexShrink: 0 }}>
        <button style={{ ...s.backBtn, padding: '0 0 10px 0' }} onClick={() => { setMode(null); setSearch(''); }}>← Back</button>
        <input style={{ ...s.input, marginBottom: 0 }} placeholder="🔍 Search your collection..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 80px' }}>
        <div style={s.sectionTitle}>{eligible.length} Bottles</div>
        {eligible.length === 0 && <div style={{ ...s.card, textAlign: 'center', padding: 24 }}><p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>No bottles match your search.</p></div>}
        {eligible.map((bottle, i) => <BottleCard key={i} bottle={bottle} avgScore={getAvgScore(scoreMap, bottle.id)} onClick={() => onChooseCollection(bottle)} />)}
      </div>
    </div>
  );

  if (mode === 'bar') return <BarTastingEntry onBack={() => setMode(null)} onStart={onChooseBar} />;

  return (
    <div style={s.screen}>
      <h2 style={{ color: COLORS.text, margin: '0 0 6px 0', fontSize: 18 }}>New Tasting Note</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 24px 0' }}>Where are you tasting?</p>
      {[
        { mode: 'collection', icon: '🥃', title: 'My Collection', sub: 'Taste a bottle you own', border: COLORS.borderStrong },
        { mode: 'bar', icon: '🍻', title: 'Bar / Tasting Event', sub: "Log a bottle you don't own", border: COLORS.border },
      ].map(opt => (
        <button key={opt.mode} onClick={() => setMode(opt.mode)} style={{ display: 'block', width: '100%', textAlign: 'left', background: `linear-gradient(135deg, ${COLORS.bgCardTop} 0%, ${COLORS.bgCard} 100%)`, border: `1px solid ${opt.border}`, borderRadius: 12, padding: 20, marginBottom: 14, cursor: 'pointer', fontFamily: 'Georgia, serif', boxShadow: '0 3px 12px rgba(0,0,0,0.45)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{opt.icon}</div>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: COLORS.text, marginBottom: 4 }}>{opt.title}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>{opt.sub}</div>
        </button>
      ))}
      {recentTastings.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={s.sectionTitle}>RECENTLY TASTED</div>
          {recentTastings.slice(0, 5).map((note, i) => {
            const isExpanded = expandedNote === i;
            const bottleForNote = bottles.find(b => b.id === note.bottle_id);
            const displayLabel = note.is_experience
              ? (note.overall_notes?.match(/^\[([^\]]+)\]/)?.[1] || 'Bar / Event')
              : (bottleForNote?.expression_name || 'Unknown Bottle');
            return (
              <div key={note.id || i} style={s.tastingNoteCard}>
                <div style={s.tastingNoteHeader} onClick={() => setExpandedNote(isExpanded ? null : i)}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.text }}>{displayLabel}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                      {formatDate(note.tasting_date)}{note.serving_style ? ` • ${note.serving_style}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {note.overall_score && (
                      <div style={{ ...s.scoreCircle, backgroundColor: getScoreColor(note.overall_score) + '22', border: `2px solid ${getScoreColor(note.overall_score)}` }}>
                        <span style={{ fontSize: 14, fontWeight: 'bold', color: getScoreColor(note.overall_score), lineHeight: 1 }}>{note.overall_score}</span>
                        <span style={{ fontSize: 7, color: COLORS.textMuted, letterSpacing: 1 }}>SCORE</span>
                      </div>
                    )}
                    {note.would_reach_again !== null && note.would_reach_again !== undefined && (
                      <span style={{ fontSize: 20 }}>{note.would_reach_again ? '👍' : '👎'}</span>
                    )}
                    <span style={{ color: COLORS.textMuted, fontSize: 16 }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isExpanded && (
                  <div>
                    <div style={s.divider} />
                    {note.nose_notes && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>NOSE</div><div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{note.nose_notes}</div></div>}
                    {note.palate_notes && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>PALATE</div><div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{note.palate_notes}</div></div>}
                    {note.finish_notes && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>FINISH</div><div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{note.finish_notes}</div></div>}
                    {note.overall_notes && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>OVERALL</div><div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{note.overall_notes}</div></div>}
                    {bottleForNote && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button style={{ ...s.secondaryBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0, flex: 1 }}
                          onClick={() => onNavigate('edit-tasting', { note, bottle: bottleForNote })}>✏️ Edit</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
};

const BarTastingEntry = ({ onBack, onStart }) => {
  const [looking, setLooking] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [form, setForm] = useState({ expression_name: '', type: 'Bourbon', proof: '', mash_bill: '' });
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLookup = async () => {
    if (!form.expression_name) { setLookupError('Enter a bottle name first'); return; }
    setLooking(true); setLookupError(''); setLookupSuccess('');
    try {
      const result = await lookupBottle(form.expression_name);
      setForm(prev => ({ ...prev, expression_name: result.expression_name || prev.expression_name, type: result.type || prev.type, proof: result.proof ? String(result.proof) : prev.proof, mash_bill: result.mash_bill || prev.mash_bill }));
      setLookupSuccess('✅ Found it! Review and correct if needed.');
    } catch (err) { setLookupError("Couldn't find it automatically — fill in the details below."); }
    setLooking(false);
  };

  return (
    <div style={s.screen}>
      {looking && <div style={s.lookupOverlay}><div style={{ fontSize: 48 }}>🔍</div><p style={{ color: COLORS.accent, fontSize: 16, fontWeight: 'bold', letterSpacing: 2, textAlign: 'center' }}>Looking Up Bottle...</p></div>}
      <button style={s.backBtn} onClick={onBack}>← Back</button>
      <h2 style={{ color: COLORS.text, margin: '0 0 4px 0', fontSize: 18 }}>Bar / Tasting Event</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>This note won't be added to your collection.</p>
      <div style={s.card}>
        <p style={s.cardTitle}>BOTTLE INFO</p>
        <label style={s.label}>Bottle Name *</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...s.input, marginBottom: 0, flex: 1 }} placeholder="e.g. Blanton's Original" value={form.expression_name} onChange={e => set('expression_name', e.target.value)} />
          <button style={{ background: `linear-gradient(135deg, ${COLORS.accentLight} 0%, ${COLORS.accent} 100%)`, color: COLORS.bg, border: 'none', borderRadius: 8, padding: '0 14px', fontFamily: 'Georgia, serif', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', opacity: looking ? 0.6 : 1 }} onClick={handleLookup} disabled={looking}>🔍 Look Up</button>
        </div>
        {lookupSuccess && <div style={{ ...s.successBanner, marginBottom: 12 }}>{lookupSuccess}</div>}
        {lookupError && <div style={{ ...s.errorBanner, marginBottom: 12 }}>⚠️ {lookupError}</div>}
        <label style={s.label}>Type</label>
        <select style={s.select} value={form.type} onChange={e => set('type', e.target.value)}>{WHISKEY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select>
        <label style={s.label}>Proof</label>
        <input style={s.input} type="number" placeholder="e.g. 93" value={form.proof} onChange={e => set('proof', e.target.value)} />
        <label style={s.label}>Mash Bill (optional)</label>
        <input style={s.input} placeholder="e.g. 75% corn, 13% rye, 12% malt" value={form.mash_bill} onChange={e => set('mash_bill', e.target.value)} />
      </div>
      <button style={s.primaryBtn} onClick={() => { if (!form.expression_name) return; onStart(form); }}>Start Tasting →</button>
    </div>
  );
};

// ── TastingScreen — Phase 1 Redesign with collapsible phase cards ─────────────
export const TastingScreen = ({ bottles, bottle, tastingNote, onNavigate, onSave, onDiscard, recentTastings = [], scoreMap }) => {
  const isEdit = !!tastingNote;
  const hasBottleContext = !!bottle;
  const draftKey = isEdit ? editDraftKey(tastingNote.id) : TASTING_DRAFT_KEY;

  const [selectedBottle, setSelectedBottle] = useState(bottle || null);
  const [barBottleInfo, setBarBottleInfo] = useState(null);
  const [choiceMade, setChoiceMade] = useState(!!bottle || isEdit);
  const [draftFound, setDraftFound] = useState(false);
  const [dbLoadDone, setDbLoadDone] = useState(false);

  const [openPhases, setOpenPhases] = useState({ when: false, color: false, nose: false, palate: false, finish: false });
  const togglePhase = (phase) => setOpenPhases(prev => ({ ...prev, [phase]: !prev[phase] }));

  const [colorStop, setColorStop] = useState(tastingNote?.color_stop || 'Amber');
  const [colorNotes, setColorNotes] = useState(tastingNote?.color_notes || '');
  const [colorClarity, setColorClarity] = useState(null);
  const [colorViscosity, setColorViscosity] = useState(null);

  const [noseBroad, setNoseBroad] = useState([]);
  const [noseSpecific, setNoseSpecific] = useState([]);
  const [noseNotes, setNoseNotes] = useState(tastingNote?.nose_notes || '');
  const [noseIntensity, setNoseIntensity] = useState(tastingNote?.nose_intensity || null);

  const [palateBroad, setPalateBroad] = useState([]);
  const [palateSpecific, setPalateSpecific] = useState([]);
  const [palateNotes, setPalateNotes] = useState(tastingNote?.palate_notes || '');
  const [mouthfeel, setMouthfeel] = useState(tastingNote?.mouthfeel_text || null);
  const [heat, setHeat] = useState(tastingNote?.heat || null);

  const [finishLength, setFinishLength] = useState(null);
  const [finishBroad, setFinishBroad] = useState([]);
  const [finishSpecific, setFinishSpecific] = useState([]);
  const [finishNotes, setFinishNotes] = useState(tastingNote?.finish_notes || '');
  const [finishFade, setFinishFade] = useState(tastingNote?.finish_fade || null);

  const [tastingDate, setTastingDate] = useState(tastingNote?.tasting_date || new Date().toISOString().split('T')[0]);
  const [servingStyle, setServingStyle] = useState(tastingNote?.serving_style || 'Neat');
  const [fillLevel, setFillLevel] = useState(tastingNote?.fill_level_at_tasting ?? bottle?.fill_level ?? 80);
  const [rating, setRating] = useState(tastingNote?.overall_score || null);
  const [wouldReachAgain, setWouldReachAgain] = useState(tastingNote?.would_reach_again !== undefined ? tastingNote.would_reach_again : null);
  const [overallNotes, setOverallNotes] = useState(tastingNote?.overall_notes || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const loadFlavorsFromDb = (callback) => {
    db.fetch('flavor_selections', 'flavor_tag,phase,flavor_category', {
      bottle_id: tastingNote.bottle_id,
      tasting_date: tastingNote.tasting_date,
    }).then(data => {
      if (!Array.isArray(data)) { if (callback) callback(); return; }
      const byCategory = (phase, cat) => data.filter(f => f.phase === phase && f.flavor_category === cat).map(f => f.flavor_tag);
      const legacy = (phase) => data.filter(f => f.phase === phase && f.flavor_category === 'General').map(f => f.flavor_tag);
      setNoseBroad([...byCategory('nose', 'broad'), ...legacy('nose')]);
      setNoseSpecific(byCategory('nose', 'specific'));
      setPalateBroad([...byCategory('palate', 'broad'), ...legacy('palate')]);
      setPalateSpecific(byCategory('palate', 'specific'));
      setFinishBroad([...byCategory('finish', 'broad'), ...legacy('finish')]);
      setFinishSpecific(byCategory('finish', 'specific'));
      const lengthTag = byCategory('finish', 'length');
      if (lengthTag.length > 0) setFinishLength(lengthTag[0]);
      else {
        const legacyFinish = legacy('finish');
        if (legacyFinish.includes('Long')) setFinishLength('Long');
        else if (legacyFinish.includes('Short')) setFinishLength('Short');
        else if (legacyFinish.includes('Medium')) setFinishLength('Medium');
      }
      if (tastingNote.mouthfeel_text) setMouthfeel(tastingNote.mouthfeel_text);
      else if (tastingNote.mouthfeel) { const mfMap = { 1: 'Light', 2: 'Light', 3: 'Medium', 4: 'Full', 5: 'Oily' }; setMouthfeel(mfMap[tastingNote.mouthfeel] || null); }
      if (tastingNote.heat) setHeat(tastingNote.heat);
      if (tastingNote.finish_fade) setFinishFade(tastingNote.finish_fade);
      if (tastingNote.nose_intensity) setNoseIntensity(tastingNote.nose_intensity);
      if (tastingNote.color_stop) setColorStop(tastingNote.color_stop);
      if (tastingNote.color_notes) setColorNotes(tastingNote.color_notes);
      if (callback) callback();
    });
  };

  useEffect(() => {
    let draftRestored = false;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d) {
          if (!isEdit && d.selectedBottle) setSelectedBottle(d.selectedBottle);
          if (!isEdit && d.barBottleInfo) setBarBottleInfo(d.barBottleInfo);
          setTastingDate(d.tastingDate || tastingNote?.tasting_date || new Date().toISOString().split('T')[0]);
          setServingStyle(d.servingStyle || tastingNote?.serving_style || 'Neat');
          setFillLevel(d.fillLevel ?? tastingNote?.fill_level_at_tasting ?? bottle?.fill_level ?? 80);
          setColorStop(d.colorStop || tastingNote?.color_stop || 'Amber');
          setColorNotes(d.colorNotes ?? tastingNote?.color_notes ?? '');
          setColorClarity(d.colorClarity || null);
          setColorViscosity(d.colorViscosity || null);
          setNoseBroad(d.noseBroad || []);
          setNoseSpecific(d.noseSpecific || []);
          setNoseNotes(d.noseNotes ?? tastingNote?.nose_notes ?? '');
          setNoseIntensity(d.noseIntensity || null);
          setPalateBroad(d.palateBroad || []);
          setPalateSpecific(d.palateSpecific || []);
          setPalateNotes(d.palateNotes ?? tastingNote?.palate_notes ?? '');
          setMouthfeel(d.mouthfeel || null);
          setHeat(d.heat || null);
          setFinishLength(d.finishLength || null);
          setFinishBroad(d.finishBroad || []);
          setFinishSpecific(d.finishSpecific || []);
          setFinishNotes(d.finishNotes ?? tastingNote?.finish_notes ?? '');
          setFinishFade(d.finishFade || null);
          setRating(d.rating || tastingNote?.overall_score || null);
          setWouldReachAgain(d.wouldReachAgain ?? tastingNote?.would_reach_again ?? null);
          setOverallNotes(d.overallNotes ?? tastingNote?.overall_notes ?? '');
          if (d.openPhases) setOpenPhases({ when: false, color: false, nose: false, palate: false, finish: false, ...d.openPhases });
          if (!isEdit) setChoiceMade(true);
          setDraftFound(true);
          draftRestored = true;
        }
      }
    } catch (e) { localStorage.removeItem(draftKey); }

    if (isEdit && !draftRestored && tastingNote?.id) {
      loadFlavorsFromDb(() => setDbLoadDone(true));
    } else {
      setDbLoadDone(true);
    }
  }, []);

  useEffect(() => {
    if (!choiceMade && !isEdit) return;
    if (isEdit && !dbLoadDone) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        selectedBottle, barBottleInfo, tastingDate, servingStyle, fillLevel,
        colorStop, colorNotes, colorClarity, colorViscosity,
        noseBroad, noseSpecific, noseNotes, noseIntensity,
        palateBroad, palateSpecific, palateNotes, mouthfeel, heat,
        finishLength, finishBroad, finishSpecific, finishNotes, finishFade,
        rating, wouldReachAgain, overallNotes, openPhases,
      }));
    } catch (e) { /* storage full */ }
  }, [choiceMade, dbLoadDone, selectedBottle, barBottleInfo, tastingDate, servingStyle, fillLevel,
      colorStop, colorNotes, colorClarity, colorViscosity,
      noseBroad, noseSpecific, noseNotes, noseIntensity,
      palateBroad, palateSpecific, palateNotes, mouthfeel, heat,
      finishLength, finishBroad, finishSpecific, finishNotes, finishFade,
      rating, wouldReachAgain, overallNotes, openPhases]);

  const clearDraftAndNavigate = () => {
    localStorage.removeItem(draftKey);
    const dest = bottle || selectedBottle;
    if (dest) onNavigate('detail', dest);
    else onNavigate('collection', null);
  };

  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setDraftFound(false);
    setOpenPhases({ when: false, color: false, nose: false, palate: false, finish: false });
    if (!isEdit) { if (onDiscard) onDiscard(); return; }
    setColorStop(tastingNote?.color_stop || 'Amber');
    setColorNotes(tastingNote?.color_notes || '');
    setColorClarity(null); setColorViscosity(null);
    setNoseNotes(tastingNote?.nose_notes || '');
    setPalateNotes(tastingNote?.palate_notes || '');
    setFinishNotes(tastingNote?.finish_notes || '');
    setRating(tastingNote?.overall_score || null);
    setWouldReachAgain(tastingNote?.would_reach_again ?? null);
    setOverallNotes(tastingNote?.overall_notes || '');
    setNoseBroad([]); setNoseSpecific([]);
    setPalateBroad([]); setPalateSpecific([]);
    setFinishBroad([]); setFinishSpecific([]);
    setFinishLength(null); setNoseIntensity(null);
    setMouthfeel(null); setHeat(null); setFinishFade(null);
    setDbLoadDone(false);
    loadFlavorsFromDb(() => setDbLoadDone(true));
  };

  if (!choiceMade) {
    return (
      <TastingChoiceScreen
        bottles={bottles} scoreMap={scoreMap || {}} recentTastings={recentTastings}
        onNavigate={onNavigate}
        onChooseCollection={(b) => { setSelectedBottle(b); setFillLevel(b.fill_level ?? 80); setChoiceMade(true); }}
        onChooseBar={(barInfo) => { setBarBottleInfo(barInfo); setChoiceMade(true); }}
      />
    );
  }

  const handleBroadToggle = (taxonomy, broadSetter, specificSetter) => (cat) => {
    broadSetter(prev => {
      if (prev.includes(cat)) {
        const tier2 = taxonomy[cat] || [];
        specificSetter(sp => sp.filter(t => !tier2.includes(t)));
        return prev.filter(c => c !== cat);
      }
      return [...prev, cat];
    });
  };

  const handleSpecificToggle = (setter) => (tag) => {
    setter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleSingleSelect = (setter, current) => (val) => {
    setter(current === val ? null : val);
  };

  const handleSave = async () => {
    if (!rating) { setError('Please provide a rating before saving'); return; }
    setSaving(true); setError('');
    try {
      const isBarTasting = !!barBottleInfo;
      const bottleId = isEdit ? tastingNote.bottle_id : (selectedBottle?.id || null);
      const mfValue = { Light: 2, Medium: 3, Full: 4, Oily: 5 }[mouthfeel] || null;

      const noteRecord = {
        bottle_id: bottleId, tasting_date: tastingDate, serving_style: servingStyle,
        fill_level_at_tasting: isBarTasting ? 100 : fillLevel,
        overall_score: rating, would_reach_again: wouldReachAgain, reason_tags: [],
        color_stop: colorStop, color_notes: colorNotes || null,
        nose_intensity: noseIntensity || null, mouthfeel_text: mouthfeel || null,
        heat: heat || null, finish_fade: finishFade || null,
        sweetness: null, oak_intensity: null, oak_type: null, spice_intensity: null,
        mouthfeel: mfValue,
        finish_length: finishLength === 'Long' ? 5 : finishLength === 'Short' ? 1 : finishLength === 'Medium' ? 3 : null,
        finish_dryness: finishBroad.includes('Dry') ? 1 : finishBroad.includes('Sweet') ? 5 : null,
        nose_notes: noseNotes, palate_notes: palateNotes, finish_notes: finishNotes,
        overall_notes: isBarTasting
          ? [`[${barBottleInfo.expression_name}${barBottleInfo.proof ? ` • ${barBottleInfo.proof} proof` : ''}${barBottleInfo.type ? ` • ${barBottleInfo.type}` : ''}${barBottleInfo.mash_bill ? ` • ${barBottleInfo.mash_bill}` : ''}]`, overallNotes].filter(Boolean).join('\n')
          : overallNotes,
        nose_score: null, palate_score: null, finish_score: null, is_experience: isBarTasting,
      };

      if (isEdit) {
        await db.update('tasting_notes', tastingNote.id, noteRecord);
        if (bottleId) await supabase.from('flavor_selections').delete().eq('bottle_id', bottleId).eq('tasting_date', tastingNote.tasting_date);
      } else {
        await db.insert('tasting_notes', noteRecord);
      }

      const allFlavors = [
        ...noseBroad.map(f => ({ flavor_tag: f, phase: 'nose', flavor_category: 'broad' })),
        ...noseSpecific.map(f => ({ flavor_tag: f, phase: 'nose', flavor_category: 'specific' })),
        ...palateBroad.map(f => ({ flavor_tag: f, phase: 'palate', flavor_category: 'broad' })),
        ...palateSpecific.map(f => ({ flavor_tag: f, phase: 'palate', flavor_category: 'specific' })),
        ...finishBroad.map(f => ({ flavor_tag: f, phase: 'finish', flavor_category: 'broad' })),
        ...finishSpecific.map(f => ({ flavor_tag: f, phase: 'finish', flavor_category: 'specific' })),
        ...(finishLength ? [{ flavor_tag: finishLength, phase: 'finish', flavor_category: 'length' }] : []),
      ];

      if (allFlavors.length > 0 && bottleId) {
        await Promise.all(allFlavors.map(({ flavor_tag, phase, flavor_category }) =>
          db.insert('flavor_selections', { bottle_id: bottleId, flavor_tag, phase, flavor_category, tasting_date: tastingDate })
        ));
      }

      setSuccess(true);
      localStorage.removeItem(draftKey);
      if (onSave) onSave();
      setTimeout(() => {
        const destination = bottle || selectedBottle;
        if (destination && !isBarTasting) onNavigate('detail', destination);
        else onNavigate('collection', null);
      }, 1500);
    } catch (err) { console.error(err); setError('Failed to save. Please try again.'); }
    setSaving(false);
  };

  const currentBottle = selectedBottle || bottle;
  const displayName = barBottleInfo?.expression_name || currentBottle?.expression_name;
  const displayProof = barBottleInfo?.proof || currentBottle?.proof;

  const colorSummary = buildColorSummary(colorStop);
  const noseSummary = buildPhaseSummary(noseBroad, noseSpecific, NOSE_FLAVORS);
  const palateSummary = buildPhaseSummary(palateBroad, palateSpecific, PALATE_FLAVORS);
  const finishSummary = buildFinishSummary(finishLength, finishBroad, finishSpecific, FINISH_FLAVORS);

  return (
    <div style={{ ...s.screen, paddingTop: 0 }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        backgroundColor: COLORS.bg,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '10px 16px 10px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <button style={{ ...s.backBtn, flexShrink: 0, padding: 0 }} onClick={clearDraftAndNavigate}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 'bold', color: COLORS.text, fontFamily: 'Georgia, serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {barBottleInfo ? '🍻' : '🥃'} {displayName}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
            {[displayProof ? `${displayProof} proof` : null, isEdit ? 'editing note' : 'tasting note', barBottleInfo ? 'bar / event' : null].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px 0' }}>
        {draftFound && (
          <div style={{ backgroundColor: COLORS.bgCard, border: `2px solid ${COLORS.red}`, borderRadius: 10, padding: '16px', marginBottom: 12, boxShadow: `0 0 16px ${COLORS.red}33` }}>
            <p style={{ color: COLORS.red, fontWeight: 'bold', fontSize: 15, margin: '0 0 6px 0', letterSpacing: 0.3 }}>
              {isEdit ? '✏️ Unsaved edit restored' : '🥃 Unfinished tasting restored'}
            </p>
            <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 14px 0', lineHeight: 1.5 }}>
              {isEdit ? 'Your previous edits are loaded. Continue where you left off or discard to revert.' : 'Your previous selections are loaded. Continue where you left off or discard to start fresh.'}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDraftFound(false)} style={{ flex: 1, padding: '12px', borderRadius: 8, border: `2px solid ${COLORS.accentLight}`, background: `linear-gradient(135deg, ${COLORS.accentLight}22, ${COLORS.accent}33)`, color: COLORS.accentLight, fontFamily: 'Georgia, serif', fontSize: 14, fontWeight: 'bold', cursor: 'pointer', letterSpacing: 0.3 }}>Continue</button>
              <button onClick={discardDraft} style={{ flex: 1, padding: '12px', borderRadius: 8, border: `1px solid ${COLORS.red}`, background: `${COLORS.red}18`, color: COLORS.red, fontFamily: 'Georgia, serif', fontSize: 13, cursor: 'pointer', letterSpacing: 0.3 }}>
                {isEdit ? 'Discard & Revert' : 'Discard & Reset'}
              </button>
            </div>
          </div>
        )}

        {success && <div style={s.successBanner}>✅ Tasting note saved! Rating: {rating}/10</div>}
        {error && <div style={s.errorBanner}>⚠️ {error}</div>}

        <PhaseCard phaseKey="color" label="When & How"
          isOpen={openPhases.when} onToggle={() => togglePhase('when')}
          summary={[
            { type: 'plain', label: new Date(tastingDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) },
            servingStyle ? { type: 'plain', label: servingStyle } : null,
            !barBottleInfo ? { type: 'plain', label: `${fillLevel}% full` } : null,
          ].filter(Boolean)}>
          <label style={s.label}>Date</label>
          <input style={s.input} type="date" value={tastingDate} onChange={e => setTastingDate(e.target.value)} />
          <label style={s.label}>Serving Style</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            {SERVING_STYLES.filter(st => st !== 'Custom').map(style => {
              const active = servingStyle === style;
              return (
                <button key={style} onClick={() => setServingStyle(style)} style={{ padding: '8px 14px', borderRadius: 20, border: `1px solid ${active ? COLORS.accent : 'rgba(144,115,85,0.35)'}`, background: active ? `linear-gradient(135deg, ${COLORS.accentLight}22, ${COLORS.accent}33)` : 'transparent', color: active ? COLORS.accentLight : COLORS.textMuted, fontSize: 13, fontFamily: 'Georgia, serif', cursor: 'pointer', fontWeight: active ? 'bold' : 'normal' }}>{style}</button>
              );
            })}
          </div>
          {!barBottleInfo && (
            <>
              <label style={{ ...s.label, marginTop: 12 }}>Fill Level at Tasting — {fillLevel}%</label>
              <input type="range" min={0} max={100} step={10} value={fillLevel} onChange={e => setFillLevel(parseInt(e.target.value))} style={{ width: '100%', accentColor: COLORS.accent }} />
            </>
          )}
        </PhaseCard>

        <PhaseCard phaseKey="color" label="Color & Appearance"
          isOpen={openPhases.color} onToggle={() => togglePhase('color')}
          summary={colorSummary}>
          <label style={s.label}>Color</label>
          <ColorSlider value={colorStop} onChange={setColorStop} stops={COLOR_STOPS} />
          <div style={{ marginTop: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: COLORS.accentLight, fontWeight: 'bold' }}>{colorStop}</span>
          </div>
          <textarea style={{ ...s.textarea, marginTop: 8 }} placeholder="Appearance notes... (optional)" value={colorNotes} onChange={e => setColorNotes(e.target.value)} />
          <ExpansionCard label="Clarity & Viscosity">
            <label style={s.label}>Clarity</label>
            <MultiChips options={CLARITY_OPTIONS} selected={colorClarity} onToggle={handleSingleSelect(setColorClarity, colorClarity)} />
            <label style={{ ...s.label, marginTop: 10 }}>Legs / Viscosity</label>
            <MultiChips options={VISCOSITY_OPTIONS} selected={colorViscosity} onToggle={handleSingleSelect(setColorViscosity, colorViscosity)} />
          </ExpansionCard>
        </PhaseCard>

        <PhaseCard phaseKey="nose" label="Nose"
          isOpen={openPhases.nose} onToggle={() => togglePhase('nose')}
          summary={noseSummary}>
          <p style={{ fontSize: 12, color: COLORS.textMuted, margin: '0 0 10px 0' }}>What's the dominant character?</p>
          <TwoTierChips taxonomy={NOSE_FLAVORS} broadSelected={noseBroad} specificSelected={noseSpecific} onBroadToggle={handleBroadToggle(NOSE_FLAVORS, setNoseBroad, setNoseSpecific)} onSpecificToggle={handleSpecificToggle(setNoseSpecific)} />
          <textarea style={{ ...s.textarea, marginTop: 12 }} placeholder="Anything specific you notice... (optional)" value={noseNotes} onChange={e => setNoseNotes(e.target.value)} />
          <ExpansionCard label="Intensity">
            <MultiChips options={NOSE_INTENSITY_OPTIONS} selected={noseIntensity} onToggle={handleSingleSelect(setNoseIntensity, noseIntensity)} />
          </ExpansionCard>
        </PhaseCard>

        <PhaseCard phaseKey="palate" label="Palate"
          isOpen={openPhases.palate} onToggle={() => togglePhase('palate')}
          summary={palateSummary}>
          <p style={{ fontSize: 12, color: COLORS.textMuted, margin: '0 0 10px 0' }}>What do you taste?</p>
          <TwoTierChips taxonomy={PALATE_FLAVORS} broadSelected={palateBroad} specificSelected={palateSpecific} onBroadToggle={handleBroadToggle(PALATE_FLAVORS, setPalateBroad, setPalateSpecific)} onSpecificToggle={handleSpecificToggle(setPalateSpecific)} />
          <label style={{ ...s.label, marginTop: 12 }}>Mouthfeel</label>
          <MultiChips options={MOUTHFEEL_OPTIONS} selected={mouthfeel} onToggle={handleSingleSelect(setMouthfeel, mouthfeel)} />
          <textarea style={{ ...s.textarea, marginTop: 12 }} placeholder="Anything specific... (optional)" value={palateNotes} onChange={e => setPalateNotes(e.target.value)} />
          <ExpansionCard label="Heat">
            <MultiChips options={HEAT_OPTIONS} selected={heat} onToggle={handleSingleSelect(setHeat, heat)} />
          </ExpansionCard>
        </PhaseCard>

        <PhaseCard phaseKey="finish" label="Finish"
          isOpen={openPhases.finish} onToggle={() => togglePhase('finish')}
          summary={finishSummary}>
          <label style={s.label}>Length</label>
          <MultiChips options={FINISH_LENGTH_OPTIONS} selected={finishLength} onToggle={handleSingleSelect(setFinishLength, finishLength)} />
          <label style={{ ...s.label, marginTop: 12 }}>Character</label>
          <TwoTierChips taxonomy={FINISH_FLAVORS} broadSelected={finishBroad} specificSelected={finishSpecific} onBroadToggle={handleBroadToggle(FINISH_FLAVORS, setFinishBroad, setFinishSpecific)} onSpecificToggle={handleSpecificToggle(setFinishSpecific)} />
          <textarea style={{ ...s.textarea, marginTop: 12 }} placeholder="What lingers? (optional)" value={finishNotes} onChange={e => setFinishNotes(e.target.value)} />
          <ExpansionCard label="Fade">
            <MultiChips options={FINISH_FADE_OPTIONS} selected={finishFade} onToggle={handleSingleSelect(setFinishFade, finishFade)} />
          </ExpansionCard>
        </PhaseCard>

        <div style={s.card}>
          <div style={{ fontSize: 9, color: COLORS.accent, letterSpacing: 3, fontFamily: 'Georgia, serif', fontWeight: 'bold', marginBottom: 10 }}>RATING</div>
          <p style={{ fontSize: 12, color: COLORS.textMuted, margin: '0 0 12px 0' }}>How was it overall? <span style={{ color: COLORS.red }}>*</span></p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setRating(n)} style={{ width: 44, height: 44, borderRadius: 8, border: `2px solid ${rating === n ? COLORS.accent : COLORS.border}`, backgroundColor: rating === n ? COLORS.accent : 'transparent', color: rating === n ? COLORS.bg : COLORS.textMuted, fontSize: 16, fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>{n}</button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: COLORS.textMuted, margin: '0 0 12px 0' }}>Would you reach for it again?</p>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button onClick={() => setWouldReachAgain(wouldReachAgain === true ? null : true)} style={{ flex: 1, padding: '14px', borderRadius: 8, border: `2px solid ${wouldReachAgain === true ? COLORS.green : COLORS.border}`, backgroundColor: wouldReachAgain === true ? COLORS.green + '22' : 'transparent', fontSize: 24, cursor: 'pointer' }}>👍</button>
            <button onClick={() => setWouldReachAgain(wouldReachAgain === false ? null : false)} style={{ flex: 1, padding: '14px', borderRadius: 8, border: `2px solid ${wouldReachAgain === false ? COLORS.red : COLORS.border}`, backgroundColor: wouldReachAgain === false ? COLORS.red + '22' : 'transparent', fontSize: 24, cursor: 'pointer' }}>👎</button>
          </div>
          <label style={s.label}>Overall Notes (optional)</label>
          <textarea style={s.textarea} placeholder="Any final thoughts..." value={overallNotes} onChange={e => setOverallNotes(e.target.value)} />
        </div>

        <button style={{ ...s.primaryBtn, opacity: rating ? 1 : 0.5 }} onClick={handleSave} disabled={saving || !rating}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Tasting Note'}
        </button>
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
};

export const WishlistScreen = () => {
  const [wishlist, setWishlist] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ bottle_name: '', priority: 'High', target_price: '', notes: '', target_store: '' });
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  useEffect(() => { db.fetch('wishlist').then(data => { if (Array.isArray(data)) setWishlist(data); }); }, []);

  const loadWishlist = async () => {
    const data = await db.fetch('wishlist');
    if (Array.isArray(data)) setWishlist(data);
  };

  const handleAdd = async () => {
    if (!form.bottle_name) return;
    setSaving(true);
    await db.insert('wishlist', { ...form, target_price: form.target_price ? parseFloat(form.target_price) : null, available: false, alert_enabled: true });
    await loadWishlist();
    setForm({ bottle_name: '', priority: 'High', target_price: '', notes: '', target_store: '' });
    setShowForm(false); setSaving(false);
  };

  const handleEditSave = async () => {
    if (!form.bottle_name) return;
    setSaving(true);
    await db.update('wishlist', editingItem.id, {
      ...form,
      target_price: form.target_price ? parseFloat(form.target_price) : null,
    });
    await loadWishlist();
    setEditingItem(null);
    setForm({ bottle_name: '', priority: 'High', target_price: '', notes: '', target_store: '' });
    setExpandedItem(null);
    setSaving(false);
  };

  const handleDelete = async (item) => {
    await db.delete('wishlist', 'id', item.id);
    await loadWishlist();
    setExpandedItem(null);
  };

  const startEdit = (w) => {
    setEditingItem(w);
    setForm({
      bottle_name: w.bottle_name || '',
      priority: w.priority || 'High',
      target_price: w.target_price ? String(w.target_price) : '',
      notes: w.notes || '',
      target_store: w.target_store || '',
    });
    setShowForm(false);
  };

  const priorityColor = (p) => p === 'High' ? COLORS.red : p === 'Medium' ? COLORS.accent : COLORS.textMuted;

  return (
    <div style={s.screen}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={s.sectionTitle}>Hunting List — {wishlist.length} Bottles</div>
        <button style={{ ...s.secondaryBtn, width: 'auto', padding: '6px 14px', marginBottom: 0, fontSize: 11 }}
          onClick={() => { setShowForm(!showForm); setEditingItem(null); }}>
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div style={s.card}>
          <p style={s.cardTitle}>ADD TO WISHLIST</p>
          <label style={s.label}>Bottle Name *</label>
          <input style={s.input} placeholder="e.g. William Larue Weller" value={form.bottle_name} onChange={e => set('bottle_name', e.target.value)} />
          <label style={s.label}>Priority</label>
          <select style={s.select} value={form.priority} onChange={e => set('priority', e.target.value)}>{['High','Medium','Low'].map(p => <option key={p} value={p}>{p}</option>)}</select>
          <label style={s.label}>Target Retail Price ($)</label>
          <input style={s.input} placeholder="e.g. 90" type="number" value={form.target_price} onChange={e => set('target_price', e.target.value)} />
          <label style={s.label}>Where To Look</label>
          <input style={s.input} placeholder="e.g. Total Wine, local shops" value={form.target_store} onChange={e => set('target_store', e.target.value)} />
          <label style={s.label}>Notes</label>
          <textarea style={s.textarea} placeholder="Why you want it..." value={form.notes} onChange={e => set('notes', e.target.value)} />
          <button style={s.primaryBtn} onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Add To Wishlist'}</button>
        </div>
      )}

      {/* Edit form */}
      {editingItem && (
        <div style={{ ...s.card, borderColor: COLORS.accent }}>
          <p style={s.cardTitle}>EDIT — {editingItem.bottle_name}</p>
          <label style={s.label}>Bottle Name *</label>
          <input style={s.input} value={form.bottle_name} onChange={e => set('bottle_name', e.target.value)} />
          <label style={s.label}>Priority</label>
          <select style={s.select} value={form.priority} onChange={e => set('priority', e.target.value)}>{['High','Medium','Low'].map(p => <option key={p} value={p}>{p}</option>)}</select>
          <label style={s.label}>Target Retail Price ($)</label>
          <input style={s.input} type="number" value={form.target_price} onChange={e => set('target_price', e.target.value)} />
          <label style={s.label}>Where To Look</label>
          <input style={s.input} value={form.target_store} onChange={e => set('target_store', e.target.value)} />
          <label style={s.label}>Notes</label>
          <textarea style={s.textarea} value={form.notes} onChange={e => set('notes', e.target.value)} />
          <button style={s.primaryBtn} onClick={handleEditSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          <button style={s.secondaryBtn} onClick={() => { setEditingItem(null); setForm({ bottle_name: '', priority: 'High', target_price: '', notes: '', target_store: '' }); }}>Cancel</button>
        </div>
      )}

      {['High','Medium','Low'].map(priority => {
        const items = wishlist.filter(w => w.priority === priority);
        if (items.length === 0) return null;
        return (
          <div key={priority}>
            <div style={{ ...s.sectionTitle, color: priorityColor(priority) }}>{priority} Priority — {items.length}</div>
            {items.map((w, i) => {
              const key = `${priority}-${i}`;
              const isExpanded = expandedItem === key;
              return (
                <div key={i} style={s.wishCard}>
                  <div style={{ cursor: 'pointer' }} onClick={() => setExpandedItem(isExpanded ? null : key)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <p style={{ ...s.bottleName, margin: '0 0 3px 0', flex: 1 }}>{w.bottle_name}</p>
                      <span style={{ color: COLORS.textMuted, fontSize: 14, marginLeft: 8 }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                    {w.target_price && <p style={{ ...s.bottleMeta, margin: '0 0 4px 0' }}>Target: ${w.target_price}</p>}
                    <span style={s.tag(priorityColor(priority))}>{priority} Priority</span>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
                      {w.target_store && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>WHERE TO LOOK</div><div style={{ fontSize: 13, color: COLORS.textMuted }}>{w.target_store}</div></div>}
                      {w.notes && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>NOTES</div><div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic' }}>{w.notes}</div></div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button style={{ ...s.secondaryBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0, flex: 1 }}
                          onClick={() => startEdit(w)}>✏️ Edit</button>
                        <button style={{ ...s.dangerBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0, flex: 1 }}
                          onClick={() => handleDelete(w)}>🗑️ Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {wishlist.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <p>Your hunting list is empty.</p>
        </div>
      )}
    </div>
  );
};


// ── TastingSearchScreen — full journal view with filters and sort ──────────────
export const TastingSearchScreen = ({ bottles, onNavigate }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scoreMin, setScoreMin] = useState(null);
  const [wouldReachFilter, setWouldReachFilter] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [expandedNote, setExpandedNote] = useState(null);
  const PAGE_SIZE = 20;

  useEffect(() => { loadNotes(); }, []);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('tasting_notes')
        .select('id, tasting_date, overall_score, would_reach_again, serving_style, fill_level_at_tasting, nose_notes, palate_notes, finish_notes, overall_notes, color_stop, color_notes, mouthfeel_text, heat, finish_fade, nose_intensity, is_experience, bottle_id')
        .eq('user_id', user.id)
        .order('tasting_date', { ascending: false });

      if (error) throw error;

      const { data: flavors } = await supabase
        .from('flavor_selections')
        .select('bottle_id, tasting_date, flavor_tag, phase, flavor_category')
        .eq('user_id', user.id);

      const flavorMap = {};
      (flavors || []).forEach(f => {
        const key = `${f.bottle_id}__${f.tasting_date}`;
        if (!flavorMap[key]) flavorMap[key] = [];
        flavorMap[key].push(f);
      });

      const enriched = (data || []).map(note => {
        const bottleRecord = bottles.find(b => b.id === note.bottle_id);
        return {
          ...note,
          flavors: flavorMap[`${note.bottle_id}__${note.tasting_date}`] || [],
          bottleName: note.is_experience
            ? (note.overall_notes?.match(/^\[([^\]]+)\]/)?.[1] || 'Bar / Event')
            : (bottleRecord?.expression_name || 'Unknown Bottle'),
          distillery: bottleRecord?.distillery || '',
        };
      });

      setNotes(enriched);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const filtered = notes.filter(note => {
    const matchSearch = !search ||
      note.bottleName?.toLowerCase().includes(search.toLowerCase()) ||
      note.distillery?.toLowerCase().includes(search.toLowerCase());
    const matchMin = scoreMin === null || (note.overall_score >= scoreMin);
    const matchReach = wouldReachFilter === null || note.would_reach_again === wouldReachFilter;
    return matchSearch && matchMin && matchReach;
  }).sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'date') cmp = new Date(a.tasting_date) - new Date(b.tasting_date);
    if (sortBy === 'score') cmp = (a.overall_score || 0) - (b.overall_score || 0);
    if (sortBy === 'name') cmp = a.bottleName.localeCompare(b.bottleName);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const paginated = filtered.slice(0, (page + 1) * PAGE_SIZE);
  const hasMore = filtered.length > paginated.length;

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir(key === 'score' ? 'desc' : key === 'date' ? 'desc' : 'asc'); }
  };
  const getSortIcon = (key) => sortBy !== key ? '' : sortDir === 'asc' ? ' ↑' : ' ↓';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {/* Filter header */}
      <div style={{ background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, padding: '10px 16px 8px', flexShrink: 0 }}>
        <input
          style={{ ...s.input, marginBottom: 10 }}
          placeholder="🔍 Search by bottle or distillery..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />

        {/* Score filter */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: COLORS.textMuted, letterSpacing: 2, textTransform: 'uppercase', flexShrink: 0, width: 44 }}>Score</span>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1, scrollbarWidth: 'none' }}>
            {[null, 5, 6, 7, 8, 9, 10].map((val, i) => {
              const label = val === null ? 'All' : `${val}+`;
              const active = val === null ? scoreMin === null : scoreMin === val;
              return (
                <button key={i} onClick={() => { setScoreMin(val); setPage(0); }}
                  style={{ ...s.filterChip(active), flexShrink: 0 }}>{label}</button>
              );
            })}
          </div>
        </div>

        {/* Would Reach Again filter */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: COLORS.textMuted, letterSpacing: 2, textTransform: 'uppercase', flexShrink: 0, width: 44 }}>Reach</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ val: null, label: 'All' }, { val: true, label: '👍' }, { val: false, label: '👎' }].map(({ val, label }) => (
              <button key={String(val)} onClick={() => { setWouldReachFilter(val); setPage(0); }}
                style={{ ...s.filterChip(wouldReachFilter === val), flexShrink: 0 }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: COLORS.textMuted, letterSpacing: 2, textTransform: 'uppercase', flexShrink: 0, width: 44 }}>Sort</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['date', 'Date'], ['score', 'Score'], ['name', 'Name']].map(([key, label]) => (
              <button key={key} onClick={() => handleSort(key)}
                style={{ ...s.filterChip(sortBy === key), flexShrink: 0 }}>{label}{getSortIcon(key)}</button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 80px' }}>
        <div style={s.sectionTitle}>
          {loading ? 'Loading...' : `${filtered.length} Tasting Note${filtered.length !== 1 ? 's' : ''}`}
        </div>

        {!loading && filtered.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📝</div>
            <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>No notes match your filters.</p>
          </div>
        )}

        {paginated.map((note, i) => {
          const isExpanded = expandedNote === i;

          const getPhase = (phase, cat) =>
            note.flavors.filter(f => f.phase === phase && (f.flavor_category === cat || f.flavor_category === 'General')).map(f => f.flavor_tag);
          const getExact = (phase, cat) =>
            note.flavors.filter(f => f.phase === phase && f.flavor_category === cat).map(f => f.flavor_tag);

          const noseBroad     = getPhase('nose', 'broad');
          const noseSpecific  = getExact('nose', 'specific');
          const palateBroad   = getPhase('palate', 'broad');
          const palateSpecific = getExact('palate', 'specific');
          const finishBroad   = getPhase('finish', 'broad');
          const finishSpecific = getExact('finish', 'specific');
          const finishLengthTag = getExact('finish', 'length')[0] || null;

          const noseSummary   = buildPhaseSummary(noseBroad, noseSpecific, NOSE_FLAVORS);
          const palateSummary = buildPhaseSummary(palateBroad, palateSpecific, PALATE_FLAVORS);
          const finishSummary = buildFinishSummary(finishLengthTag, finishBroad, finishSpecific, FINISH_FLAVORS);
          const hasPhaseData  = noseBroad.length > 0 || palateBroad.length > 0 || finishBroad.length > 0;
          const allFlavors    = note.flavors.map(f => f.flavor_tag);

          return (
            <div key={note.id || i} style={s.tastingNoteCard}>
              <div style={s.tastingNoteHeader} onClick={() => setExpandedNote(isExpanded ? null : i)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 'bold', color: COLORS.text, marginBottom: 2 }}>{note.bottleName}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {formatDate(note.tasting_date)}{note.serving_style ? ` · ${note.serving_style}` : ''}{note.color_stop ? ` · ${note.color_stop}` : ''}
                  </div>
                  {/* Collapsed flavor preview */}
                  {!isExpanded && hasPhaseData && (
                    <div style={{ marginTop: 4 }}>
                      <MiniSummary summary={[...noseSummary, ...palateSummary].slice(0, 3)} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  {note.overall_score && (
                    <div style={{ ...s.scoreCircle, backgroundColor: getScoreColor(note.overall_score) + '22', border: `2px solid ${getScoreColor(note.overall_score)}` }}>
                      <span style={{ fontSize: 14, fontWeight: 'bold', color: getScoreColor(note.overall_score), lineHeight: 1 }}>{note.overall_score}</span>
                      <span style={{ fontSize: 7, color: COLORS.textMuted, letterSpacing: 1 }}>SCORE</span>
                    </div>
                  )}
                  {note.would_reach_again !== null && note.would_reach_again !== undefined && (
                    <span style={{ fontSize: 18 }}>{note.would_reach_again ? '👍' : '👎'}</span>
                  )}
                  <span style={{ color: COLORS.textMuted, fontSize: 14 }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {isExpanded && (
                <div>
                  <div style={s.divider} />
                  {note.color_stop && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>COLOR</div>
                      <span style={s.tag(COLORS.textMuted)}>{note.color_stop}</span>
                      {note.color_notes && <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 6, lineHeight: 1.6 }}>{note.color_notes}</div>}
                    </div>
                  )}
                  {noseSummary.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>NOSE</div>
                      <MiniSummary summary={noseSummary} />
                      {note.nose_intensity && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Intensity: {note.nose_intensity}</div>}
                      {note.nose_notes && <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 6, lineHeight: 1.6 }}>{note.nose_notes}</div>}
                    </div>
                  )}
                  {palateSummary.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>PALATE</div>
                      <MiniSummary summary={palateSummary} />
                      {note.mouthfeel_text && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Mouthfeel: {note.mouthfeel_text}</div>}
                      {note.heat && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>Heat: {note.heat}</div>}
                      {note.palate_notes && <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 6, lineHeight: 1.6 }}>{note.palate_notes}</div>}
                    </div>
                  )}
                  {finishSummary.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>FINISH</div>
                      <MiniSummary summary={finishSummary} />
                      {note.finish_fade && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Fade: {note.finish_fade}</div>}
                      {note.finish_notes && <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 6, lineHeight: 1.6 }}>{note.finish_notes}</div>}
                    </div>
                  )}
                  {!hasPhaseData && allFlavors.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 6 }}>FLAVORS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{allFlavors.map(f => <span key={f} style={s.tag()}>{f}</span>)}</div>
                    </div>
                  )}
                  {note.overall_notes && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: COLORS.accent, letterSpacing: 2, marginBottom: 4 }}>OVERALL</div>
                      <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>{note.overall_notes}</div>
                    </div>
                  )}
                  {note.bottle_id && (
                    <button
                      style={{ ...s.secondaryBtn, fontSize: 11, padding: '8px 14px', marginBottom: 0 }}
                      onClick={() => onNavigate('detail', { id: note.bottle_id, expression_name: note.bottleName })}>
                      → View Bottle
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {hasMore && (
          <button style={{ ...s.secondaryBtn, marginTop: 8 }} onClick={() => setPage(p => p + 1)}>
            Load More ({filtered.length - paginated.length} remaining)
          </button>
        )}
        <div style={{ height: 20 }} />
      </div>
    </div>
  );
};
   